import React, { useState, useEffect, useRef } from 'react';
import { HexColorPicker } from 'react-colorful';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';

import SubmitInputField from 'components/FormFields/SubmitInputField';
import DropdownButton from 'components/Button/DropdownButton/DropdownButton';
import APDropdown from 'components/Dropdown/APDropdown';
import { APTooltipProvider, useTooltip } from 'components/Tooltip';

import './color-picker.scss';

const PALETTE_MODE = 'palette';
const CUSTOM_MODE = 'custom';
const DEFAULT_COLOR = '#000000';
const MAX_NUMBER_SAVED_COLORS = 30;
const LIMIT_MESSAGE = `You have reached your max of 30 custom colors.
  Please return to PALETTE view to delete custom colors to add more.`;

const isHexColor = (value) => {
  const regex = /^#[0-9A-F]{6}$/i;
  return regex.test(value);
};

const ColorPicker = ({
  color = DEFAULT_COLOR,
  defaultColor,
  savedColors = [],
  usedColors = [],
  hasClearOption = false,
  handleColorSelect,
  handleColorSave,
  handleHexChange,
  handleSavedColorDelete,
}) => {
  const [mode, setMode] = useState(PALETTE_MODE);
  const [deleteMode, setDeleteMode] = useState(false);
  const [showModeSelect, setShowModeSelect] = useState(false);
  const [localColor, setLocalColor] = useState(color);
  const [inputValue, setInputValue] = useState(localColor);
  const [eyedropSupported, setEyedropSupported] = useState(false);

  // Save function must exist to save colors and only allow a max number of saved colors.
  const isAtColorLimit = savedColors.length >= MAX_NUMBER_SAVED_COLORS;
  const canSave = !!handleColorSave && !isAtColorLimit;

  useEffect(() => {
    // Check if browser supports the eyedropper.
    if (window.EyeDropper) {
      setEyedropSupported(true);
    }
  }, []);

  useEffect(() => {
    setInputValue(localColor);
  }, [localColor]);

  const onClear = (e) => {
    e.stopPropagation();
    handleColorSelect(e, defaultColor ?? null);
  };

  const handleInputChange = (e) => {
    if (!e.target) return;

    let { value } = e.target;
    // Always add # to the input value.
    if (value === '') {
      value = '#';
    }
    // Always prepend # if it doesn't exist.
    if (value.substring(0, 1) !== '#') {
      value = `#${value}`;
    }
    setInputValue(value);
  };

  const onModeChange = (e, value) => {
    e.stopPropagation();
    setMode(value);
    setShowModeSelect(false);
  };

  const selectColor = (e, value) => {
    setLocalColor(value);
    handleColorSelect(e, value);
  };

  const saveColor = (e, value) => {
    selectColor(e, value);
    if (canSave) handleColorSave(e, value);
  };

  const handleSavedColorClick = (e, hex) => {
    if (deleteMode) {
      handleSavedColorDelete(e, hex);
    } else {
      selectColor(e, hex);
    }
  };

  const onHexChange = (value) => {
    setLocalColor(value);
    if (handleHexChange) handleHexChange(value);
  };

  const pickColor = async () => {
    // eslint-disable-next-line
    const eyeDropper = new EyeDropper();

    try {
      const pickedColor = await eyeDropper.open();
      const { sRGBHex } = pickedColor;
      if (sRGBHex) {
        setLocalColor(sRGBHex);
      }
    } catch (error) {
      // eslint-disable-next-line
      console.log(error);
    }
  };

  const dropdownTrigger = (
    <DropdownButton
      className="button--unstyled mode-select-dropdown__trigger"
      showIcon
    >
      {mode === PALETTE_MODE ? PALETTE_MODE : 'picker'}
    </DropdownButton>
  );

  return (
    <APTooltipProvider>
      <div
        className={clsx('color-picker', `color-picker--${mode}`)}
      >
        {mode === PALETTE_MODE ? (
          <>
            {hasClearOption && (
              <div className="color-picker__section">
                <div className="color-picker__clear">
                  <button
                    className="button--unstyled color-picker__button color-picker__button--clear"
                    onClick={onClear}
                  >
                    <FontAwesomeIcon icon={['fa', 'ban']} /> None
                  </button>
                </div>
              </div>
            )}
            <div className="color-picker__section">
              <div className="color-picker__grid">
                {defaultColors.map(hex => (
                  <ColorCircle
                    className={clsx(
                      'color-picker__button',
                      'button--unstyled',
                      'color-circle',
                      hex === color && 'selected',
                    )}
                    onClick={(e) => selectColor(e, hex)}
                    style={{ backgroundColor: hex }}
                    hex={hex}
                  />
                ))}
              </div>
            </div>
            {(savedColors && savedColors.length > 0) && (
              <div className="color-picker__section">
                <div className="color-picker__section-header color-picker__section-header--actionable">
                  Custom colors
                  {handleSavedColorDelete && (
                    <button
                      className="button--unstyled"
                      onClick={() => setDeleteMode(!deleteMode)}
                    >
                      {deleteMode ? 'Done' : 'Edit'}
                    </button>
                  )}
                </div>
                <div className="color-picker__grid color-picker__grid--saved">
                  {savedColors.map(hex => (
                    <ColorCircle
                      className={clsx(
                        'color-picker__button',
                        'button--unstyled',
                        'color-circle',
                        hex === color && 'selected',
                        deleteMode && 'color-circle--delete',
                      )}
                      onClick={(e) => handleSavedColorClick(e, hex)}
                      style={{ backgroundColor: hex }}
                      hex={hex}
                    />
                  ))}
                </div>
              </div>
            )}
            {(usedColors && usedColors.length > 0) && (
              <div className="color-picker__section">
                <div className="color-picker__section-header">Used colors</div>
                <div className="color-picker__grid color-picker__grid--saved">
                  {usedColors.map(hex => (
                    <ColorCircle
                      className={clsx(
                        'color-picker__button',
                        'button--unstyled',
                        'color-circle',
                        hex === color && 'selected',
                      )}
                      onClick={(e) => selectColor(e, hex)}
                      style={{ backgroundColor: hex }}
                      hex={hex}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="color-picker__section">
            <div className="color-picker__input">
              <SubmitInputField
                value={inputValue}
                onChange={handleInputChange}
                onBlur={(e) => {
                  const { value } = e.target;
                  if (isHexColor(value)) {
                    setInputValue(value);
                    setLocalColor(value);
                  } else {
                    setInputValue(localColor);
                  }
                }}
              />
              <div
                className="color-picker__input-indicator color-circle"
                style={{ backgroundColor: localColor }}
              />
              <ColorPickerSubmitButton
                className="button"
                onClick={(e) => saveColor(e, inputValue)}
                disabled={!canSave}
                isAtColorLimit={isAtColorLimit}
              >
                Save
              </ColorPickerSubmitButton>
            </div>
            <HexColorPicker
              color={localColor}
              onChange={onHexChange}
            />
            <button
              disabled={!eyedropSupported}
              className="eyedropper button--unstyled"
              onClick={pickColor}
            >
              <FontAwesomeIcon icon={['fa', 'eye-dropper']} />
            </button>
          </div>
        )}

        <div className="color-picker__section">
          <div className="color-picker__selector">
            <APDropdown
              isOpen={showModeSelect}
              show={() => setShowModeSelect(true)}
              dismiss={() => setShowModeSelect(false)}
              triggerButton={dropdownTrigger}
              name="color-picker-mode-select"
              scrollLockOnOpen={false}
              placement="top-start"
              root={null}
            >
              <button
                className={clsx(
                  'color-picker__selector-dropdown',
                  'button--unstyled',
                  mode === PALETTE_MODE && 'selected',
                )}
                onClick={(e) => onModeChange(e, PALETTE_MODE)}
              >
                {PALETTE_MODE}
              </button>
              <button
                className={clsx(
                  'color-picker__selector-dropdown',
                  'button--unstyled',
                  mode === CUSTOM_MODE && 'selected',
                )}
                onClick={(e) => onModeChange(e, CUSTOM_MODE)}
              >
                {CUSTOM_MODE}
              </button>
            </APDropdown>
          </div>
        </div>
      </div>
    </APTooltipProvider>
  );
};

const ColorCircle = (props) => {
  const {
    setCurrentTitle,
    setAnchor,
  } = useTooltip();
  const ref = useRef();
  const { hex } = props;

  const updateTooltip = () => {
    setCurrentTitle(hex.toUpperCase());
    setAnchor(ref.current);
  };

  const resetTooltip = () => {
    setCurrentTitle(null);
    setAnchor(null);
  };

  return (
    <button
      ref={ref}
      {...props}
      onMouseEnter={updateTooltip}
      onMouseMove={updateTooltip}
      onMouseLeave={resetTooltip}
    />
  );
};

const useSubmitButtonTooltipProps = (isAtColorLimit) => {
  const ref = useRef();
  const { setCurrentTitle, setAnchor } = useTooltip();

  const onMouseEnter = () => {
    setCurrentTitle(LIMIT_MESSAGE);
    setAnchor(ref.current);
  };

  const onMouseMove = () => {
    setCurrentTitle(LIMIT_MESSAGE);
    setAnchor(ref.current);
  };

  const onMouseLeave = () => {
    setCurrentTitle(null);
    setAnchor(null);
  };

  if (!isAtColorLimit) return {};

  return {
    ref,
    onMouseEnter,
    onMouseMove,
    onMouseLeave,
  };
};

const ColorPickerSubmitButton = (props) => {
  const { isAtColorLimit, ...rest } = props;
  const tooltipProps = useSubmitButtonTooltipProps(isAtColorLimit);

  // Add a span to register pointer events because disabled button has pointer-events:none.
  return (
    <span
      {...tooltipProps}
    >
      <button
        {...rest}
      />
    </span>
  );
};

// For testing
/*
const savedColors = [
  '#4A85E8',
  '#0000FF',
  '#9900FF',
  '#FF00FF',
  '#E6B8AF',
  '#F4CCCC',
  '#FDE5CD',
  '#FFF3CC',
  '#DD7D6B',
  '#EB9899',
  '#F9CB9C',
  '#FFE598',
  '#B6D7A8',
  '#A2C4CA',
  '#A4C2F4',
  '#9EC5E9',
  '#B4A7D6',
];
*/

const defaultColors = [
  '#000000',
  '#434343',
  '#666666',
  '#999999',
  '#B7B7B7',
  '#CCCCCC',
  '#D9D9D9',
  '#EFEFEF',
  '#F3F3F3',
  '#FFFFFF',
  '#980000',
  '#FF0000',
  '#FF9800',
  '#FFFF00',
  '#00FF00',
  '#00FFFF',
  '#4A85E8',
  '#0000FF',
  '#9900FF',
  '#FF00FF',
  '#E6B8AF',
  '#F4CCCC',
  '#FDE5CD',
  '#FFF3CC',
  '#D9EBD3',
  '#D1E0E3',
  '#C9DBF8',
  '#CFE2F3',
  '#D9D3E9',
  '#EBD1DC',
  '#DD7D6B',
  '#EB9899',
  '#F9CB9C',
  '#FFE598',
  '#B6D7A8',
  '#A2C4CA',
  '#A4C2F4',
  '#9EC5E9',
  '#B4A7D6',
  '#D5A6BD',
  '#CC4124',
  '#E06665',
  '#F7B16B',
  '#FFD966',
  '#92C47D',
  '#75A5AF',
  '#6D9EEC',
  '#6EA8DC',
  '#8E7CC3',
  '#C27BA0',
  '#A61B00',
  '#CC0000',
  '#E69038',
  '#F1C231',
  '#6AA84E',
  '#44818E',
  '#3B77D8',
  '#3C84C6',
  '#674EA7',
  '#A64C79',
  '#841F0B',
  '#990000',
  '#B45F04',
  '#BF9000',
  '#38761C',
  '#114F5C',
  '#0F55CC',
  '#095294',
  '#351A75',
  '#741A47',
  '#5B0D00',
  '#660000',
  '#783E02',
  '#7F5F00',
  '#274E11',
  '#0A343C',
  '#1B4487',
  '#053762',
  '#1F104C',
  '#4C0F2F',
];

export default ColorPicker;
